import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/lib/AuthContext";
import { Card, Btn, Pill, Modal, Empty, Notice, PageHeader, Field, inputCls } from "@/dashboard/ui";
import PhoneInput from "@/components/PhoneInput";
import { PhoneCall, Search, Plus, ShieldCheck, ArrowRightLeft, Trash2 } from "lucide-react";

const STATUS_TONE = {
  active: "green", verified: "green", pending_regulatory: "amber",
  unverified: "amber", pending_verification: "amber", disabled: "grey", released: "grey",
};
const TYPE_LABEL = { local: "Local", mobile: "Mobile", toll_free: "Toll-free" };

const NUMBER_OPTIONS = [
  {
    id: "new", title: "Get a new business number",
    body: "Choose a UK number through Savy Kerno and it is provisioned through our telephony provider (Twilio) — rental and per-minute prices are shown before you confirm. UK numbers need a short regulatory step (your business details and a UK address) before they activate.",
    note: "Search and request below. Provisioning runs in simulated mode until live purchasing is approved.",
  },
  {
    id: "forward-all", title: "Keep your number — forward all calls",
    body: "Your customers keep dialling your existing number; your carrier forwards every call to your Savy Kerno number, where the AI answers. You set this up with your current carrier (usually an 'immediate divert' setting or a code such as *21* followed by your Savy Kerno number).",
    note: "Carrier-side setting — we provide the destination number once your Savy Kerno number is active.",
  },
  {
    id: "port", title: "Keep your number — AI answers all calls",
    body: "Port your existing number to our telephony provider so the number itself is answered by your AI agent — no carrier settings needed afterwards. UK porting needs a letter of authorisation and account details from your current provider and typically takes 4–6 weeks.",
    note: "Porting opens when live number provisioning is approved — we'll guide you through it then.",
  },
  {
    id: "missed-only", title: "Keep your number — AI answers missed calls only",
    body: "Conditional call forwarding: your phone rings first, and only busy, unanswered or unreachable calls divert to the AI. Most UK carriers support this with codes such as *61* (no answer), *67* (busy) and *62* (unreachable) followed by your Savy Kerno number.",
    note: "Carrier-side setting — exact codes vary by network; check with your carrier. Your customers never see a different number.",
  },
];

export default function TelephonyNumbers() {
  const [numbers, setNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [simulated, setSimulated] = useState(true);
  const [loading, setLoading] = useState(true);

  // search wizard
  const [searchOpen, setSearchOpen] = useState(false);
  const [nType, setNType] = useState("local");
  const [contains, setContains] = useState("");
  const [areaCode, setAreaCode] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // regulatory modal
  const [regFor, setRegFor] = useState(null); // number doc
  const [reg, setReg] = useState({ business_name: "", business_address: "", rep_name: "", rep_email: "", rep_phone: "+44" });
  const [busy, setBusy] = useState(false);
  // forwarding editor
  const [fwdFor, setFwdFor] = useState(null);
  const [fwdMode, setFwdMode] = useState("ai");
  const [fwdTo, setFwdTo] = useState("+44");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [n, a, p] = await Promise.all([
        api.get("/telephony/numbers"), api.get("/voice-agents"), api.get("/telephony/pricing"),
      ]);
      setNumbers(n.data.numbers || []);
      setAgents(a.data || []);
      setPricing(p.data.pricing);
      setSimulated(p.data.simulated);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doSearch = async () => {
    setSearching(true);
    try {
      const { data } = await api.get("/telephony/numbers/search", {
        params: { number_type: nType, contains, area_code: areaCode },
      });
      setResults(data.results || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSearching(false);
    }
  };

  const request = async (r) => {
    setBusy(true);
    try {
      const { data } = await api.post("/telephony/numbers/provision", { number: r.number, number_type: r.number_type });
      toast.success(data.notice);
      setSearchOpen(false);
      setResults([]);
      await load();
      const doc = data.number;
      setReg({ business_name: "", business_address: "", rep_name: "", rep_email: "", rep_phone: "+44" });
      setRegFor(doc); // straight into the regulatory step
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const submitReg = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/telephony/numbers/${regFor.id}/regulatory`, reg);
      toast.success(data.number.status === "active" ? "Number active" : "Bundle submitted");
      setRegFor(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (nid, agentId) => {
    try {
      await api.post(`/telephony/numbers/${nid}/assign`, { agent_id: agentId });
      toast.success(agentId ? "Agent assigned" : "Agent unassigned");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveFwd = async () => {
    setBusy(true);
    try {
      await api.put(`/telephony/numbers/${fwdFor.id}/forwarding`,
        { mode: fwdMode, forward_to: fwdMode === "forward" ? fwdTo : "" });
      toast.success("Call handling updated");
      setFwdFor(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const release = async (n) => {
    if (!window.confirm(`Release ${n.number}? This returns the number and stops its rental.`)) return;
    try {
      await api.post(`/telephony/numbers/${n.id}/release`);
      toast.success("Number released");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="telephony-numbers-page">
      <PageHeader title="Phone Numbers" sub="Search, provision and manage UK business numbers for your AI agents. Until live telephony is approved and connected, every step here is clearly labelled SIMULATED.">
        <Btn data-testid="get-number-btn" onClick={() => setSearchOpen(true)}>
          <Plus size={15} /> Get a number
        </Btn>
      </PageHeader>

      {simulated && (
        <Notice testid="telephony-simulated-notice" title="Simulated provisioning"
          body="Number search, purchase, regulatory approval and release currently run against a simulated provider. No real numbers are purchased and no charges occur. Live Twilio provisioning switches on only after owner approval." />
      )}

      <div className="mt-4" data-testid="number-options">
        <h3 className="mb-2 text-xs font-bold uppercase tracking-[0.15em] text-neutral-400">Your number options</h3>
        <div className="grid gap-3 sm:grid-cols-2">
          {NUMBER_OPTIONS.map((o) => (
            <Card key={o.id} className="p-4" data-testid={`number-option-${o.id}`}>
              <p className="text-sm font-semibold text-[#111111]">{o.title}</p>
              <p className="mt-1.5 text-xs leading-relaxed text-neutral-600">{o.body}</p>
              <p className="mt-2 rounded-lg bg-neutral-50 px-2.5 py-1.5 text-[11px] text-neutral-500">{o.note}</p>
            </Card>
          ))}
        </div>
      </div>

      {loading ? null : numbers.length === 0 ? (
        <Empty testid="numbers-empty" icon={PhoneCall} title="No phone numbers yet"
          body="Get a UK number, complete the regulatory details, assign it to an AI agent and it can answer calls." />
      ) : (
        <div className="mt-4 space-y-3">
          {numbers.map((n) => (
            <Card key={n.id} className="p-4" data-testid={`number-row-${n.number}`}>
              <div className="flex flex-wrap items-center gap-3">
                <div className="min-w-40">
                  <p className="text-sm font-semibold text-[#111111]">{n.number}</p>
                  <p className="text-xs text-neutral-500">
                    {TYPE_LABEL[n.number_type] || "Manually registered"}
                    {n.monthly_rental_usd != null && ` · $${n.monthly_rental_usd}/mo list`}
                  </p>
                </div>
                <Pill tone={STATUS_TONE[n.status] || "grey"}>{n.status.replace(/_/g, " ")}</Pill>
                {n.simulated && <Pill tone="amber">SIMULATED</Pill>}
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  {n.status === "pending_regulatory" && (
                    <Btn size="sm" data-testid={`regulatory-btn-${n.id}`} onClick={() => { setRegFor(n); }}>
                      <ShieldCheck size={14} /> Regulatory details
                    </Btn>
                  )}
                  <select
                    data-testid={`assign-agent-${n.id}`}
                    className={inputCls + " !w-auto text-xs"}
                    value={n.agent_id || ""}
                    onChange={(e) => assign(n.id, e.target.value)}
                  >
                    <option value="">No agent assigned</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.agent_name}</option>)}
                  </select>
                  <Btn size="sm" variant="ghost" data-testid={`forwarding-btn-${n.id}`}
                    onClick={() => { setFwdFor(n); setFwdMode(n.forwarding?.mode || "ai"); setFwdTo(n.forwarding?.forward_to || "+44"); }}>
                    <ArrowRightLeft size={14} /> {n.forwarding?.mode === "forward" ? "Forwarding" : "AI answers"}
                  </Btn>
                  {["active", "pending_regulatory"].includes(n.status) && (
                    <Btn size="sm" variant="ghost" data-testid={`release-btn-${n.id}`} onClick={() => release(n)}>
                      <Trash2 size={14} /> Release
                    </Btn>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {searchOpen && (
        <Modal title="Get a UK number" onClose={() => setSearchOpen(false)} testid="number-search-modal">
          <div className="mb-3 flex gap-2">
            {Object.keys(TYPE_LABEL).map((t) => (
              <button key={t} data-testid={`type-${t}`} onClick={() => setNType(t)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${nType === t ? "bg-[#111111] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
                {TYPE_LABEL[t]} {pricing && `· $${pricing.monthly_rental[t]}/mo`}
              </button>
            ))}
          </div>
          <div className="flex gap-2">
            <input className={inputCls} placeholder="Contains digits (optional)" value={contains}
              data-testid="search-contains" onChange={(e) => setContains(e.target.value)} />
            {nType === "local" && (
              <input className={inputCls} placeholder="Area code e.g. 161" value={areaCode}
                data-testid="search-area-code" onChange={(e) => setAreaCode(e.target.value)} />
            )}
            <Btn data-testid="search-numbers-btn" onClick={doSearch} disabled={searching}>
              <Search size={14} /> {searching ? "Searching…" : "Search"}
            </Btn>
          </div>
          <p className="mt-2 text-[11px] text-neutral-500">
            Prices are Twilio UK list prices (USD): inbound ${pricing?.inbound_per_min[nType]}/min. Review before confirming.
          </p>
          <div className="mt-3 space-y-2">
            {results.map((r) => (
              <div key={r.number} className="flex items-center gap-3 rounded-xl border border-neutral-100 p-3" data-testid={`result-${r.number}`}>
                <div>
                  <p className="text-sm font-semibold">{r.number}</p>
                  <p className="text-[11px] text-neutral-500">{r.region} · ${r.monthly_rental_usd}/mo + ${r.inbound_per_min_usd}/min</p>
                </div>
                <Btn size="sm" className="ml-auto" data-testid={`request-${r.number}`} disabled={busy} onClick={() => request(r)}>
                  Request
                </Btn>
              </div>
            ))}
          </div>
        </Modal>
      )}

      {regFor && (
        <Modal title={`Regulatory details — ${regFor.number}`} onClose={() => setRegFor(null)} testid="regulatory-modal">
          <p className="mb-3 text-xs text-neutral-500">
            UK numbers require a regulatory bundle (Ofcom rules): your business details, a UK address and an authorised
            representative. {simulated && "This submission is SIMULATED and approves instantly in development."}
          </p>
          <div className="space-y-3">
            <Field label="Business name"><input className={inputCls} data-testid="reg-business-name" value={reg.business_name} onChange={(e) => setReg({ ...reg, business_name: e.target.value })} /></Field>
            <Field label="UK business address"><input className={inputCls} data-testid="reg-address" value={reg.business_address} onChange={(e) => setReg({ ...reg, business_address: e.target.value })} /></Field>
            <Field label="Authorised representative"><input className={inputCls} data-testid="reg-rep-name" value={reg.rep_name} onChange={(e) => setReg({ ...reg, rep_name: e.target.value })} /></Field>
            <Field label="Representative work email"><input className={inputCls} data-testid="reg-rep-email" value={reg.rep_email} onChange={(e) => setReg({ ...reg, rep_email: e.target.value })} /></Field>
            <Field label="Representative phone"><PhoneInput value={reg.rep_phone} onChange={(v) => setReg({ ...reg, rep_phone: v })} /></Field>
            <Btn data-testid="reg-submit" onClick={submitReg} disabled={busy}>{busy ? "Submitting…" : "Submit & activate"}</Btn>
          </div>
        </Modal>
      )}

      {fwdFor && (
        <Modal title={`Call handling — ${fwdFor.number}`} onClose={() => setFwdFor(null)} testid="forwarding-modal">
          <div className="space-y-3">
            <div className="flex gap-2">
              {[["ai", "AI agent answers"], ["forward", "Forward to my phone"]].map(([m, label]) => (
                <button key={m} data-testid={`fwd-mode-${m}`} onClick={() => setFwdMode(m)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${fwdMode === m ? "bg-[#111111] text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`}>
                  {label}
                </button>
              ))}
            </div>
            {fwdMode === "forward" && (
              <Field label="Forward calls to" hint="Your own phone — calls go straight through, no AI.">
                <PhoneInput value={fwdTo} onChange={setFwdTo} />
              </Field>
            )}
            <Btn data-testid="fwd-save" onClick={saveFwd} disabled={busy}>{busy ? "Saving…" : "Save"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
